import type { CarrierLogoProps } from './types'

interface LogoColors {
  [key: string]: { light: string; dark: string }
}

/**
 * Utility to handle logo theming with CSS light-dark() and default sizing
 *
 * Dynamically generates CSS variables based on the keys provided.
 * Each key becomes `--logo-{key}` using CSS light-dark() function for automatic theme support.
 *
 * Sets a default width of "4" (16px), maintaining aspect ratio.
 * This ensures consistent sizing across all contexts.
 *
 * @example
 * const props = getLogoProps(inputProps, {
 *   primary: { light: '#472f92', dark: '#ffffff' },
 *   secondary: { light: '#999', dark: '#eee' }
 * })
 * // Creates: --logo-primary and --logo-secondary with light-dark() values
 */
export function getLogoProps(props: CarrierLogoProps, colors?: LogoColors): CarrierLogoProps {
  const { style, w, width, ...restProps } = props

  const cssVars: Record<string, string> = {}

  // Use CSS light-dark() function for automatic theme support without hooks
  if (colors) {
    Object.entries(colors).forEach(([key, value]) => {
      cssVars[`--logo-${key}`] = `light-dark(${value.light}, ${value.dark})`
    })
  }

  // Default to w="4" (16px) if no width or w prop is provided
  const computedWidth = w !== undefined ? w : width !== undefined ? width : '4'

  return {
    ...restProps,
    w: computedWidth,
    style: {
      ...cssVars,
      ...style,
    } as React.CSSProperties,
  }
}

/** @deprecated Use getLogoProps instead - this is no longer a hook */
export const useLogoProps = getLogoProps
